import { Modal, Spin, Progress, Button } from "antd";

/**
 * ProgressModal
 * props:
 *  - loading: bool (uploading phase)
 *  - processing: bool (ML processing phase)
 *  - activeStep: number (1..totalSteps) - optional, can be provided by redux
 *  - totalSteps: number (defaults to 4)
 *  - onClose: fn
 *  - disableClose: bool
 */
const ProgressModal = ({
  loading = false,
  processing = false,
  activeStep: activeStepProp,
  totalSteps = 4,
  onClose,
  disableClose = true,
  onCancel,
  children,
}) => {
  const visible = !!loading || !!processing;

  // derive active step: prefer prop, else 1 for upload or processing start
  let activeStep = typeof activeStepProp === "number" ? activeStepProp : 1;
  if (!processing && loading) activeStep = 0; // during upload we show upload UI (no numbered steps)

  const percent = processing
    ? Math.round((Math.min(Math.max(activeStep, 1), totalSteps) / totalSteps) * 100)
    : 0;

  const handleCancel = () => {
    if (disableClose) return;
    if (typeof onClose === "function") onClose();
  };

  // step messages exactly as user requested
  const stepMessages = [
    "Dataset Registered Successfully",
    "Preprocessing Experimental Data",
    "Typing Columns — Pattern Recognition Active",
    "KNN Imputation in Progress",
  ];

  return (
    <Modal
      title={
        <div className="text-white">
          {loading
            ? "🧙‍♂️ Wizard at work... Upload potion bubbling"
            : processing
            ? "Processing"
            : "Progress"}
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      destroyOnHidden
      centered
      styles={{
        content: {
          backgroundColor: "#1f2937", // gray-800
          border: "1px solid #374151", // gray-700
        },
        header: {
          backgroundColor: "#1f2937", // gray-800
        },
        mask: {
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          backgroundColor: "rgba(15, 23, 42, 0.6)",
        },
      }}
      maskClosable={!disableClose}
      closable={!disableClose}
    >
      <div style={{ textAlign: "center" }}>
        {loading && (
          <>
            <div style={{ marginBottom: 12 }}>
              <Spin tip={<span className="text-gray-300">Initializing upload...</span>} />
            </div>
            <div style={{ marginBottom: 8, color: "#9ca3af" }}>
              Your data is brewing in the cloud cauldron!
            </div>
            {onCancel && (
              <div style={{ marginTop: 24 }}>
                <Button danger onClick={onCancel}>
                  Cancel Operation
                </Button>
              </div>
            )}
          </>
        )}

        {processing && (
          <>
            <div style={{ marginBottom: 12 }}>
              <Spin
                tip={
                  <span className="text-gray-300">
                    {activeStep === 2
                      ? "Preprocessing (this may take ~20s)..."
                      : "Processing..."}
                  </span>
                }
              />
            </div>

            <Progress
              percent={percent}
              status={activeStep < totalSteps ? "active" : "normal"}
              style={{ marginBottom: 12 }}
              trailColor="#374151"
              format={(percent) => <span className="text-gray-300">{percent}%</span>}
            />

            <div style={{ marginBottom: 12, color: "#d1d5db" }}>
              Step {Math.min(activeStep, totalSteps)} of {totalSteps}
            </div>

            <div style={{ textAlign: "left", margin: "0 auto", maxWidth: 560 }}>
              {stepMessages.map((msg, idx) => {
                const stepIndex = idx + 1;
                const isDone = stepIndex < activeStep;
                const isActive = stepIndex === activeStep;

                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 0",
                      borderRadius: 6,
                      background: isActive ? "rgba(255,255,255,0.05)" : "transparent",
                      color: isDone ? "#34d399" : isActive ? "white" : "#6b7280",
                      fontWeight: isActive ? 600 : 500,
                    }}
                  >
                    <div style={{ width: 28, textAlign: "center" }}>
                      {isDone ? "✅" : isActive ? "⏳" : "◻️"}
                    </div>
                    <div>
                      <div style={{ lineHeight: 1.1 }}>{msg}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {children && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 8,
                  background: "rgba(255,255,255,0.05)",
                }}
              >
                {children}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ProgressModal;
